import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector} from 'react-redux';
import ChatMessage from './ChatMessage';
import { useAuth } from '@clerk/clerk-react';
import {  Tooltip } from "flowbite-react";
import { HiChevronDown, HiChevronUp, HiOutlineArrowsExpand, HiPencilAlt, HiOutlineRefresh } from "react-icons/hi";
import { useProjectService, useChatService } from '../../../services';
import { setBigChatboxToggle,setLastActiveChatId } from '../../../redux/slices/chat';
import { setProjectStats } from '../../../redux/slices/projects';
import { getTimeZoneDate } from "../../../helpers/data-format";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ChatInterface({project_id,handleToggleChatBox,showChatBox,...rest}) {
  const dispatch = useDispatch()
  const { getToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMinimize, setMinimize] = useState(false);
  const [isAgentic, setAgenticToggle] = useState(false);
  const messagesEndRef = useRef(null);
  const inputTextRef = useRef(null);
  const { projectStatsData, projectStatsLoading } = useSelector((state) => state.projects)
  const { 
      lastActiveChatId,chatMessageById,chatSubquestions,chatSubquestionsLoading
    } = useSelector((state) => state.chatMessages)
  
  const { fetchProjectStats, } = useProjectService();
  const { getLastActiveQuestions, handleChatSubquestions,handleoldChatSubquestions,getoldLastActiveQuestions } = useChatService();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({block: "end", behavior: "smooth"});
  };

  useEffect(scrollToBottom, [messages,projectStatsData]);

  useEffect(()=>{
    const query = new URLSearchParams(window.location.search);
    const isnewproject = query.get('isnewproject') || "false"
    setMessages([])
    dispatch(setProjectStats({}))
    if(showChatBox){
      setAgenticToggle(false)
      fetchProjectStats(project_id,isnewproject)
      handleoldChatSubquestions({
        project_id,
        isnewproject
      })
    }
    inputTextRef?.current?.focus();
  },[showChatBox])
  
  useEffect(()=>{
    if(chatMessageById?.length){
      const checkIsAgenticChat = chatMessageById.filter(data=>data.agent_name)
      if(checkIsAgenticChat.length){
        setAgenticToggle(true)
      }
      setMessages(chatMessageById)
    }
    inputTextRef?.current?.focus();
  },[chatMessageById])

  const handleSendMessage = async (event) => {
      event.preventDefault();
      inputTextRef?.current?.focus();
      const token = await getToken({ template: 'main' });
      
      if (!inputText.trim()) return; // Prevent sending empty messages

      // Reset textarea height to original state
      if (inputTextRef.current) {
        inputTextRef.current.style.height = '40px';
      }
      
      // Get a snapshot of the current messages + the user's message
      // to send to the server to get an answer
      const userMessage = { text: inputText, isBot: false };

      const body ={
        project_id,
        text: inputText,
      }
      if(lastActiveChatId){
        body["chat_id"] =lastActiveChatId
      }    

      // Add a new empty bot message to the UI
      const botMessage = { text: '', is_bot: true };
      setMessages([...messages, userMessage, botMessage]);
      setInputText('');
      setLoading(true)

      // Send the user's message to the server and wait for a response.
      // This response will be streamed to this component.
      const response = await fetch(`${API_BASE_URL}/handle-query?module=${isAgentic ? "projectAgent " : "project"}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',"authorization" : `Bearer ${token}`  },
        responseType: 'stream',
        body: JSON.stringify(body),
      });

      if (!response.body){
        setLoading(false)
        return;
      }
      
      if(!lastActiveChatId){
        // getLastActiveQuestions(project_id)
        getoldLastActiveQuestions(project_id)
      }
      
      // Set up the infrastructure to stream the response data
      // eslint-disable-next-line no-undef
      let decoder = new TextDecoderStream();
      const reader = response.body.pipeThrough(decoder).getReader()    
      let accumulatedAnswer = ""

      while (true) {
        var { value, done } = await reader.read();
        if (done) break;
        accumulatedAnswer += value;

        const isAgenticRes = accumulatedAnswer.split("*#")
        
        // eslint-disable-next-line no-loop-func
        setMessages(currentHistory => {
          const updatedHistory = [...currentHistory]
          const lastChatIndex = updatedHistory.length - 1
          updatedHistory[lastChatIndex] = {
            ...updatedHistory[lastChatIndex],
            text: isAgenticRes.length > 1 ? isAgenticRes[isAgenticRes.length-1] : accumulatedAnswer,
            isBot:true,
            agentName : isAgenticRes.length > 1 ? isAgenticRes[0] : null
          }
          return updatedHistory
        })
      }
      setLoading(false)
      
      return false;
    
  };

  

  const handleMinimize = () =>{
    setMinimize(!showMinimize)
  }
  const handleMaximize = () =>{
    dispatch(setBigChatboxToggle(true))
  }
  const handleQuestionClick = (data) =>{
    setInputText(data);
    const textArea = inputTextRef.current;
    if(textArea){
      setTimeout(()=>{
        textArea.style.height = 'inherit';
        const newHeight = Math.min(textArea.scrollHeight, 200); // Limit to max-height
        textArea.style.height = `${newHeight}px`;
        textArea.focus();
      },10)
      
    }
  }

  const handleCreateNewChatClick = () => {
    dispatch(setLastActiveChatId(''))
    setMessages([])
    setAgenticToggle(false)
}

  const handleQuestionRefresh = () => {
    const query = new URLSearchParams(window.location.search);
    const isnewproject = query.get('isnewproject') || "false"
    handleoldChatSubquestions({
      project_id,
      isnewproject
    })
  }

  const {stats={},due_date,on_track_status,show_stats,message="",questions=[]} = projectStatsData || {}
  const {stage='',project_name=''} = rest
  
  // Chat input handling functions
  const handleInputChange = (e) => {
    if(loading) return
    setInputText(e.target.value);
    // Auto-resize with scroll
    e.target.style.height = 'inherit';
    const newHeight = Math.min(e.target.scrollHeight, 200); // Limit to max-height
    e.target.style.height = `${newHeight}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage(e);
  };

  const handleSimulationToggle = (e) => {
    e.preventDefault();
    const checkIsAgenticChat = messages.filter(data=>data.agent_name)
      if(!checkIsAgenticChat.length && !lastActiveChatId){
        setAgenticToggle(!isAgentic)
      }
  }

  return (
    <div className="w-full relative flex justify-center items-center chat-container">
            <div className="chatBoxContainer w-full  border dark:border-gray-700"> 
              <div className="bg-gray-300  dark:bg-slate-700 dark:text-white text-sm text-green-600 px-5 py-5 chat-header flex">
                <span onClick={handleMinimize} className="w-full">{!loading ? <><span>Chat with CadenceAI</span></> :"Loading"}</span>
                <span className="ml-auto flex gap-2">
                <div className="ml-auto flex relative">
                  {/* Commented out simulation toggle button */}
                  {/* <div className="text-white font-normal text-[10px] absolute top-[-20px] w-[55px]">{isAgentic ? <span>Agentic <sup>Beta</sup></span> : "Default"}</div>
                  <div
                      id="Simulation"
                      className={`md:w-11 md:h-5 w-8 h-5 flex items-center  bg-white border dark:border-slate-200 rounded-full p-1 cursor-pointer`}
                      onClick={handleSimulationToggle}
                  >
                      <div
                          className={
                              "md:w-4 md:h-4 h-3 w-3 rounded-full shadow-md transform duration-300 ease-in-out bg-gray-600" +
                              (isAgentic ? "transform translate-x-5 bg-green-600" : "")
                          }
                      >

                      </div>
                  </div> */}
              </div>
                  <span onClick={handleCreateNewChatClick} className="text-lg xs:hidden" title="Create New Chat"><HiPencilAlt /></span>
                  <span onClick={handleMaximize} className="text-lg xs:hidden"  title="Maximize Chat box"><HiOutlineArrowsExpand /></span>
                  <span onClick={handleMinimize} className="text-lg" >{showMinimize ? <HiChevronUp /> : <HiChevronDown />} </span>
                </span>
              </div>
              {!showMinimize ? <div className="chatContainer bg-gray-200 dark:bg-slate-800 overflow-auto p-2">
                {projectStatsLoading  ? <div className="flex items-center flex-col gap-3 justify-between mb-2 w-[640px]">
                    <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                    <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                    <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                    <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                    <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                </div> :
                <div className="dark:bg-slate-700 bg-white p-4 rounded-md mb-5 text-[13px]">
                    {message ? <p className="p-1 text-gray-500 mb-5 ">
                        <div>Welcome to CadenceProAI, your project planning AI assistant!</div>
                        <div>{message.split("<PROJECT_NAME>")[0] || ""}</div>
                        <strong className="dark:text-white text-gray-700"> {project_name}</strong> 
                        {message.split("<PROJECT_NAME>")[1] || ""}
                      </p> : 
                      <p className="p-1 text-gray-500 mb-5 ">Welcome to CadenceProAI, your project planning AI assistant! Here's your project summary of your project : <strong className="dark:text-white text-gray-700"> {project_name}</strong></p>
                    }
                    {stage === "executing" ?  <p className="p-1 text-gray-500 mb-5">You are <strong className={`${on_track_status === true ? "text-green-500": "text-red-500"}`}>{`${on_track_status === true ? "on track": "at risk"}`}</strong> to finishing by due date <strong className="dark:text-white">{getTimeZoneDate(due_date,"LL")}</strong></p>
                      : " "
                      // <p className="p-1 text-gray-500 mb-5">Your Project is in <strong className="dark:text-white text-gray-700"> {stage}</strong> stage.</p>
                    }
                    {show_stats && <> 
                      <p className="p-1 text-gray-500 ml-5 mb-5" >
                          <strong className="dark:text-white">{stats.running_tasks > 0 ? stats.running_tasks : "0 "} tasks</strong> are actively worked upon.
                      </p>
                      <p className="p-1 text-gray-500 ml-5 mb-5">
                          <strong className="dark:text-white">{stats.delayed_tasks > 0 ? stats.delayed_tasks : "0 "} tasks</strong> are delayed.  <strong className="dark:text-white">{stats.delayed_critical_tasks > 0 ? stats.delayed_critical_tasks : "0 "} tasks</strong> are in critical path.
                      </p>
                      <p className="p-1 text-gray-500 ml-5 mb-5">
                          <strong className="dark:text-white">{stats.unallocated_tasks.live > 0 ? stats.unallocated_tasks.live : "0 "} tasks</strong>  can't be allocated for missing resources in live mode
                      </p>
                      <p className="p-1 text-gray-500 ml-5 mb-5">
                          <strong className="dark:text-white">{stats.waiting_tasks > 0 ? stats.waiting_tasks : "0 "} tasks</strong> waiting to be picked up
                      </p>
                    </>}
                </div>}
                {<div className="flex gap-2 items-center p-2 text-xs dark:text-white text-gray-700">Example things you can do <span onClick={handleQuestionRefresh}><HiOutlineRefresh className='text-lg'/></span></div>}
                <div className="w-full flex flex-wrap rounded-md mb-4">
                    {!chatSubquestionsLoading && chatSubquestions && chatSubquestions.length && chatSubquestions.map((question)=>{
                      return (
                        <Tooltip placement="bottom" className="" content={`Try this prompt`}>
                        <button 
                          onClick={()=>{
                            handleQuestionClick(question.prompt)
                          }} 
                          className="text-base font-normal font-['Inter'] m-1 ml-0 rounded-md text-left bg-gray-300  text-gray-700  dark:bg-slate-600  dark:text-white p-2 border border-gray-600 dark:border-gray-800 shadow-sm" 
                          title={question.question}
                        >
                          {question.question}
                        </button>
                        </Tooltip>
                      )
                    })}
                  </div>
                {messages.map((message, index) => (
                  <ChatMessage key={index} message={message} />
                ))}
                {loading ? ( <div className="my message">
                    <span className="jumping-dots">
                    <span className="dot-1"></span>
                    <span className="dot-2"></span>
                    <span className="dot-3"></span>
                    </span>
                  </div>) 
                : ""}
                
                  
                  <div ref={messagesEndRef} />
              </div> :""}
              {!showMinimize ? <div className={`mt-auto p-2 px-4 dark:bg-slate-800 bg-gray-100 border-t-[1px] dark:border-gray-700`}>
                <form className="chat-input" onSubmit={handleSubmit}>
                  <textarea
                    rows="1"
                    placeholder="Type a question and press enter ..."
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    ref={inputTextRef}
                    disabled={loading}
                    className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 resize-none"
                  />
                </form>
              </div>:""}
            </div>
         <div onClick={handleToggleChatBox} className="absolute top-[-8px] right-0 z-40 cursor-pointer bg-red-600 rounded-full w-4 h-4 text-xs flex items-center justify-center">X</div>
      </div>
  )
}

export default ChatInterface;